import { ICounter } from "@/types/db/transaction";
import { LocalStorageKeys } from "@/services/storage.service";
import POSConfigModule from "@/store/modules/pos-config";
import PrinterConfigModule from "@/store/modules/printers-config";
import { watchEffect } from "vue";
import CashFloatModule from "@/store/modules/cash-float";
import { getParameterByName, QueryParameters } from "./common";
import AppModule from "@/store/modules/app";
import CustomersModule from "@/store/modules/customers";
import DeviceModule from "@/store/modules/device";
import CommonModule from "@/store/modules/common";
import OrderModule from "@/store/modules/order";
import { LogService } from "@/services/logging";
import { checkManualHardwareUuid } from "../vue-helpers/common";
import { generateHardwareHashV2 } from "./device";
import FiskalyModule from "@/store/modules/fiskaly";
import Intercom from "@intercom/messenger-js-sdk";
import { getUserAccessToken } from "@/utils/helpers/auth";
import UserModule from "@/store/modules/user";
import { IntercomService } from "@smeetz/utils-common";
import ReceiptModule from "@/store/modules/receipt";
import ComplianceModule from "@/store/modules/compliance";
import { isFrenchCompliant } from "@/utils/vue-helpers/compliance";

/**
 * Restores all store modules from localStorage.
 */
export function restoreAllStoreModules() {
  restorePOSConfigFields();
  restoreCashFloatFields();
  restorePrinterConfigFields();
  restoreAppFields();
  restoreDeviceFields();
  restoreReceiptFields();
  restoreCommonFields();
  restoreOrderFields();
  restoreFiskalyFields();
  restoreCustomerFields();
  restoreComplianceFields();
}

/**
 * Sets up watchEffects to persist store changes to localStorage.
 * Only used by main window - customer display is read-only.
 */
function persistAllStoreModules() {
  watchEffect(persistPOSConfigFields);
  watchEffect(persistCashFloatFields);
  watchEffect(persistPrinterConfigFields);
  watchEffect(persistAppFields);
  watchEffect(persistDeviceFields);
  watchEffect(persistReceiptFields);
  watchEffect(persistCommonFields);
  watchEffect(persistOrderFields);
  watchEffect(persistFiskalyFields);
  watchEffect(persistCustomerFields);
  watchEffect(persistComplianceFields);
}

/**
 * Initializes store modules with necessary data.
 */
export async function initStoreModules() {
  restoreAllStoreModules();
  persistAllStoreModules();

  const electronAPI = window.electronAPI;

  if (electronAPI) {
    // get hardware info from Electron and set them to the state
    LogService.info(`Capturing hardware info`);
    const hardwareInfo = await electronAPI.deviceInfo();
    const manualHardwareUuid = localStorage.getItem(
      LocalStorageKeys.manualHardwareUuid
    );
    if (manualHardwareUuid) {
      LogService.info(
        `Hardware info captured with manualHardwareUuid, attached.`,
        {
          params: {
            hardwareInfo: hardwareInfo,
            manualHardwareUuid: manualHardwareUuid,
          },
        }
      );
    } else {
      LogService.info(`Hardware info captured, attached.`, {
        params: hardwareInfo,
      });
    }
    console.log("----hardwareInfo");
    console.log(hardwareInfo);

    if (!DeviceModule.canHaveManualHardwareUuid) {
      console.log("-----this device doesnt have a manualHardwareUuid");
      // So this is a new group with a device that we didn't manually set hardwareuuid in localStorage
      // Lets consider deviceId as deviceId + cpuId
      if (hardwareInfo.cpuId) {
        // Lets consider deviceId as deviceId + cpuId
        LogService.info(
          `CpuId captured, manualHardwareUuid not captured, going to update deviceId as attached.`,
          {
            params: {
              oldDeviceId: hardwareInfo.id,
              newDeviceId: hardwareInfo.id + "__" + hardwareInfo.cpuId,
            },
          }
        );
        hardwareInfo.id = hardwareInfo.id + "__" + hardwareInfo.cpuId;
      }
    } else {
      console.log("-----this device might have a manualHardwareUuid");
    }

    // Compute v2 hardware hash (excludes MAC address so network changes don't
    // invalidate the device fingerprint).
    if (hardwareInfo?.hardwareHashDetails?.details) {
      hardwareInfo.hardwareHashDetails.hashV2 = await generateHardwareHashV2(
        hardwareInfo.hardwareHashDetails.details
      );
    }

    console.log("----after update hardwareInfo");
    console.log(hardwareInfo);

    // set hardware info to the state and localStorage
    DeviceModule.setHardwareInfo(hardwareInfo);

    // We started manually injecting hardware uuid in localStorage of devices using TeamViewer
    // we need to call below function to rectify hardware uuid if needed (in case user refreshes the page)
    checkManualHardwareUuid();
  }

  ///////// If this is auto-tests user then lets repull receiptCounter from remote and set it to the state
  // Why? In auto-tests it is running in a chrome browser, not in Electron
  // and we're setting DeviceModule manually and reloading the page
  // but only after a full login, so we need the below to repull the receiptCounter using the device data manually set for the auto tests user
  // But aren't we doing this for all users? I mean when app refresh we repull receiptCounter from remote using device data?
  // Because from the moment the user login we pull receiptCounter from remote and then with every order session done we do the following:
  // locally increment --> then PATCH remote --> then periodically monitor
  // And if in the middle when the user refreshes the page we pull remote counter we will be messing up the monitoring logic
  const isAutoTestsUser =
    POSConfigModule.groupInfo?.id === 16632 &&
    import.meta.env.VITE_ENV === "test";
  if (isAutoTestsUser) {
    console.log(
      "-------- Auto-tests user detected, pulling receiptCounter from remote"
    );
    LogService.info(
      "Auto-tests user detected, pulling receiptCounter from remote"
    );
    // Counter logic:
    // Lets get the counter corresponding to this device
    try {
      LogService.info(
        `Receipt Counter: fetching receipt counter for this devie uuid: ${String(
          DeviceModule.hardwareLocation?.deviceUid
        )}`
      );
      const currentReceiptCounter: ICounter =
        await ReceiptModule.fetchReceiptCounter(
          String(DeviceModule.hardwareLocation?.deviceUid)
        );
      // Lets set it into the state
      LogService.info(
        `Receipt Counter: setting attached receipt counter for thethe device uuid: ${String(
          DeviceModule.hardwareLocation?.deviceUid
        )}`,
        { currentReceiptCounter }
      );
      ReceiptModule.setReceiptCounter(currentReceiptCounter);
    } catch (err) {
      LogService.info(
        "Receipt Counter: An error occurred trying to fetch receipt counter",
        {
          err,
        }
      );
    }
  }
  /////////

  // Query parameters
  const gid = getParameterByName(QueryParameters.groupId);
  if (gid) {
    AppModule.setGID(Number(gid));
  }
}

/**
 * Whenever clicked:
 * 1- Gets Intercom user token using cognito access token
 * 2- Initializes Intercom with user token and without company data
 * NOTE: We don't pass company data as we don't have a way to match with existing companies in Intercom
 * We need to find a way to do that in future
 */
export async function initializeIntercomWithoutCompanyData(): Promise<void> {
  console.log("--------- initializing Intercom");
  const intercomOpts = {
    app_id: "olka6zru",
    user_id: String(UserModule.user.cognito_id),
    name: UserModule.user.fullname,
    email: UserModule.getUserEmail,
    cognito_user_id: String(UserModule.user.cognito_id),
    isAdmin: UserModule.isAdmin,
    channel: "POS",
    // created_at: await getCreateAtToken()
  };

  Intercom(intercomOpts);

  // // Now lets hide the launcher
  // hideIntercomLauncher();
}

export async function initializeIntercomWithCompanyData(): Promise<void> {
  console.log("--------- initializing Intercom");
  const authAccessTokenCognito = getUserAccessToken();

  /// NEW Way from Igor
  const intercomService = new IntercomService({
    environment: String(import.meta.env.VITE_ENV), // dev | test | prod
    groupId: String(POSConfigModule.groupId), // group ID /*String(7573),*/
    groupName: POSConfigModule.groupInfo?.name || "",
    accessToken: String(authAccessTokenCognito),
  });
  await intercomService.init({
    custom_launcher_selector: "#lanch",
    hide_default_launcher: true,
  });
}

/**
 * Kills Intercom session
 */
export function hideIntercom(): void {
  if (window.Intercom) {
    LogService.info(`Hiding Intercom chatbot after logout`);
    console.log("------- Hiding Intercom chatbot after logout");
    // If Intercom is already initialized, just show it
    // @ts-ignore
    window.Intercom("hide");
    LogService.info(`Intercom chatbot is now hidden`);
    console.log("--------- Intercom chatbot is now hidden");
  }
  // const container = document.querySelector("#intercom-container");
  // const luncher = document.querySelector(".intercom-launcher");

  // if (container !== null) {
  //   container.setAttribute("style", "display:none !important");
  // }

  // if (luncher !== null) {
  //   luncher.setAttribute("style", "display:none !important");
  // }
}

/**
 * Executes when pos config fields are updated
 * persists fields in localStorage
 */
export function persistPOSConfigFields(): void {
  localStorage.setItem(
    LocalStorageKeys.PosConfig,
    JSON.stringify({
      selectedGrid: POSConfigModule.selectedGrid,
      selectedConfig: POSConfigModule.selectedConfig,
      groupInfo: POSConfigModule.groupInfo,
      posConfigs: POSConfigModule.posConfigs,
      paymentSettings: POSConfigModule.getPaymentSettings,
      defaultPaymentMethodOverriden:
        POSConfigModule.defaultPaymentMethodOverriden,
      // FR (NF525): the option is removed for French-compliant groups — never
      // persist an enabled flag, including one written by an older build.
      completeWithBalance: isFrenchCompliant.value
        ? false
        : POSConfigModule.completeBookingWithBalance,
      openCashDrawer: POSConfigModule.openCashDrawer,
      autoScanTickets: POSConfigModule.autoScanTickets,
      groupCurrencies: POSConfigModule.groupCurrencies,
      configPictureUrl: POSConfigModule.configPictureUrl,
      companyLegalInfo: POSConfigModule.companyLegalInfo,
    })
  );
}

/**
 * Executes when Device fields are updated.
 * persists fields in localStorage
 */
export function persistDeviceFields(): void {
  const deviceInfo = DeviceModule.deviceInfo;
  const locations = DeviceModule.locations;
  const hardwareInfo = DeviceModule.hardwareInfo;
  const hardwareLocation = DeviceModule.hardwareLocation;
  const devices = DeviceModule.devices;
  // const receiptCounter = DeviceModule.receiptCounter;
  localStorage.setItem(
    LocalStorageKeys.Device,
    JSON.stringify({
      deviceInfo,
      hardwareInfo,
      locations,
      hardwareLocation,
      devices,
      // receiptCounter,
    })
  );
}

/**
 * Executes when Device fields are updated.
 * persists fields in localStorage
 */
export function persistReceiptFields() {
  const receiptCounter = ReceiptModule.receiptCounter;
  localStorage.setItem(
    LocalStorageKeys.Receipt,
    JSON.stringify({
      receiptCounter,
    })
  );
}

/**
 * Executes when printers are updated
 * persists fields in localStorage
 */
export function persistPrinterConfigFields(): void {
  const printer = PrinterConfigModule.printerOption;
  const ticketType = PrinterConfigModule.ticketType;
  const labelPrinter = PrinterConfigModule.labelPrinter;
  const ticketPrinter = PrinterConfigModule.ticketPrinter;
  const isLabelPrintingEnabled = PrinterConfigModule.isLabelPrintingEnabled;
  const isTicketPrintingEnabled = PrinterConfigModule.isTicketPrintingEnabled;
  const autoPrintReceipt = PrinterConfigModule.autoPrintReceipt;

  const isOrderSummaryReceiptEnabled =
    PrinterConfigModule.isOrderSummaryReceiptEnabled;
  const orderSummaryReceiptPrinter =
    PrinterConfigModule.orderSummaryReceiptPrinter;
  const receiptPrinter = PrinterConfigModule.receiptPrinter;
  localStorage.setItem(
    LocalStorageKeys.PrinterConfig,
    JSON.stringify({
      printerOption: printer,
      ticketType: ticketType,
      labelPrinter: labelPrinter,
      ticketPrinter: ticketPrinter,
      isLabelPrintingEnabled: isLabelPrintingEnabled,
      isTicketPrintingEnabled: isTicketPrintingEnabled,
      autoPrintReceipt: autoPrintReceipt,
      isOrderSummaryReceiptEnabled,
      orderSummaryReceiptPrinter,
      receiptPrinter,
    })
  );
}

/**
 * Persists AppModule fields to local storage on update
 */
export function persistAppFields(): void {
  const gid = AppModule.gid;
  localStorage.setItem(
    LocalStorageKeys.App,
    JSON.stringify({
      gid,
      lastUpdateDate: AppModule.lastUpdateDate,
      autoUpdateCount: AppModule.autoUpdateCount,
      dismissedReleaseBannerForUserIds:
        AppModule.dismissedReleaseBannerForUserIds,
    })
  );
}

/**
 * Restores AppModule using data present in localStorage;
 */
export function restoreAppFields(): void {
  const appFields = JSON.parse(
    localStorage.getItem(LocalStorageKeys.App) || "{}"
  );
  if (appFields) {
    AppModule.restore(appFields);
  }
}

/**
 * Restores POSConfig module using data present in localStorage.
 */
export function restorePOSConfigFields(): void {
  const posConfigFields = JSON.parse(
    localStorage.getItem(LocalStorageKeys.PosConfig) || "{}"
  );
  if (posConfigFields) {
    POSConfigModule.restore(posConfigFields);
  }
}

/**
 * Restores Device module using data present in localStorage.
 */
export function restoreDeviceFields(): void {
  const deviceFields = JSON.parse(
    localStorage.getItem(LocalStorageKeys.Device) || "{}"
  );
  if (deviceFields) {
    DeviceModule.restore(deviceFields);
  }
}

/**
 * Restores Receipt module using data present in localStorage.
 */
export function restoreReceiptFields() {
  const receiptFields = JSON.parse(
    localStorage.getItem(LocalStorageKeys.Receipt) || "{}"
  );
  if (receiptFields) {
    ReceiptModule.restore(receiptFields);
  }
}

/**
 * Persists CommonModule fields to local storage on update
 */
export function persistCommonFields(): void {
  const countries = CommonModule.countries;
  const userCurrentCountry = CommonModule.userCurrentCountry;
  const languages = CommonModule.languages;
  const isCustomerDisplayOpened = CommonModule.isCustomerScreenOpened;
  const ticketGroupLoadedDates = CommonModule.ticketGroupLoadedDates;
  localStorage.setItem(
    LocalStorageKeys.Common,
    JSON.stringify({
      countries,
      userCurrentCountry,
      languages,
      isCustomerDisplayOpened,
      ticketGroupLoadedDates,
    })
  );
}

/**
 * Restores Common module using data present in localStorage.
 */
export function restoreCommonFields(): void {
  const commonFields = JSON.parse(
    localStorage.getItem(LocalStorageKeys.Common) || "{}"
  );
  // console.log("commonFields");
  // console.log(commonFields);
  if (commonFields) {
    CommonModule.restore(commonFields);
  }
}

/*
 * Restores PrinterConfig module using data present in localStorage.
 */
export function restorePrinterConfigFields(): void {
  const printerConfigFields = JSON.parse(
    localStorage.getItem(LocalStorageKeys.PrinterConfig) || "{}"
  );

  if (printerConfigFields) {
    PrinterConfigModule.restore(printerConfigFields);
  }
}

/**
 * Executes when cash float fields are updated
 * persists fields in localStorage
 */
export async function persistCashFloatFields(): Promise<void> {
  const {
    cashFloat,
    transfersAmount,
    sumOfCashIn,
    sumOfCashOut,
    previousCashfloat,
    initialTransferDone,
    gainLossSyncedCurrencies,
  } = CashFloatModule;

  localStorage.setItem(
    LocalStorageKeys.CashFloat,
    JSON.stringify({
      cashFloat,
      transfersAmount,
      sumOfCashIn,
      sumOfCashOut,
      previousCashfloat,
      initialTransferDone,
      gainLossSyncedCurrencies,
    })
  );
}

/**
 * Restores Cash float module using data present in localStorage.
 */
export function restoreCashFloatFields(): void {
  const cashFloatFields = JSON.parse(
    localStorage.getItem(LocalStorageKeys.CashFloat) || "{}"
  );
  if (cashFloatFields) {
    CashFloatModule.restore(cashFloatFields);
  }
}

/**
 * Persists OrderModule fields to local storage on update
 */
export function persistOrderFields(): void {
  const orderTableConfig = OrderModule.orderTableConfig;
  const dateOfVisitFilter = OrderModule.dateOfVisitFilter;
  const selectedSortingOption = OrderModule.selectedSortingOption;
  const selectedSortingDirection = OrderModule.selectedSortingDirection;
  localStorage.setItem(
    LocalStorageKeys.Order,
    JSON.stringify({
      orderTableConfig,
      dateOfVisitFilter,
      selectedSortingOption,
      selectedSortingDirection,
    })
  );
}

/**
 * Restores OrderModule using data present in localStorage;
 */
export function restoreOrderFields(): void {
  const orderFields = JSON.parse(
    localStorage.getItem(LocalStorageKeys.Order) || "{}"
  );
  if (orderFields) {
    OrderModule.restore(orderFields);
  }
}

/**
 * Persists FiskalyModule fields to local storage on update
 */
export function persistFiskalyFields(): void {
  const signedReceiptsQrCodes = FiskalyModule.signedReceiptsQrCodes;
  const transactionsInProgress = FiskalyModule.transactionsInProgress;
  const lastTransaction = FiskalyModule.lastTransaction;
  const unsyncedTransactions = FiskalyModule.unsyncedTransactions;
  localStorage.setItem(
    LocalStorageKeys.Fiskaly,
    JSON.stringify({
      signedReceiptsQrCodes,
      transactionsInProgress,
      lastTransaction,
      unsyncedTransactions,
    })
  );
}

/**
 * Restores FiskalyModule using data present in localStorage;
 */
export function restoreFiskalyFields(): void {
  const fiskalyFields = JSON.parse(
    localStorage.getItem(LocalStorageKeys.Fiskaly) || "{}"
  );
  if (fiskalyFields) {
    FiskalyModule.restore(fiskalyFields);
  }
}

/**
 * Persists CustomerModule fields to local storage on update
 */
export function persistCustomerFields(): void {
  const selectedCustomers = CustomersModule.selectedCustomers;
  localStorage.setItem(
    LocalStorageKeys.Customer,
    JSON.stringify({
      selectedCustomers,
    })
  );
}

/**
 * Restores CustomerModule using data present in localStorage;
 */
export function restoreCustomerFields(): void {
  const customerFields = JSON.parse(
    localStorage.getItem(LocalStorageKeys.Customer) || "{}"
  );
  if (customerFields) {
    CustomersModule.restore(customerFields);
  }
}

export function persistComplianceFields() {
  const { transactionNumber } = ComplianceModule;
  localStorage.setItem(
    LocalStorageKeys.Compliance,
    JSON.stringify({
      transactionNumber,
    })
  );
}

export function restoreComplianceFields() {
  const complianceFields = JSON.parse(
    localStorage.getItem(LocalStorageKeys.Compliance) || "{}"
  );
  if (complianceFields) {
    ComplianceModule.restore(complianceFields);
  }
}
